import * as React from 'react';
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { PiX } from 'react-icons/pi';
import { cn } from '@ais-ui/utils';
import { Button } from './Button';
import { InfiniteScroll } from './InfiniteScroll';
import { Textarea } from './Textarea';
import type { IconType } from './icons/types';

export interface AutocompleteSuggestion {
  /** The text to insert when the suggestion is chosen. */
  value: string;
  /** The human readable label shown in the list. */
  label: string;
  /** Optional short badge shown before the label (e.g. a category). */
  badge?: string;
  /** Optional longer description shown to the right of the label. */
  description?: string;
  /** Optional leading icon. */
  icon?: IconType | null;
}

export interface SuggestionContext {
  value: string;
  cursorPosition: number;
  /** Whether the list was opened by typing a trigger char or manually (Ctrl+Space). */
  openedBy: 'type' | 'manual';
}

export interface SuggestionInsertResult {
  value: string;
  cursorPosition: number;
  /** Keep the suggestion list open after inserting. Defaults to false. */
  keepOpen?: boolean;
}

export interface SuggestionTextareaProps extends Omit<
  React.ComponentProps<'textarea'>,
  'value' | 'onChange' | 'onSelect'
> {
  value: string;
  onChange: (value: string) => void;
  /** Characters that auto-open the list when typed (e.g. ['%']). */
  openTriggerChars?: string[];
  /** Compute the suggestions for the current textarea state. */
  getSuggestions: (ctx: SuggestionContext) => AutocompleteSuggestion[];
  /** Apply a chosen suggestion, returning the new value and caret position. */
  applySuggestion: (
    suggestion: AutocompleteSuggestion,
    ctx: SuggestionContext,
  ) => SuggestionInsertResult;
  headerLabel?: string;
  emptyLabel?: string;
  /** Notified whenever the suggestion list opens or closes. */
  onOpenChange?: (open: boolean) => void;
}

const ITEM_HEIGHT = 36;
const MAX_VISIBLE_ITEMS = 5;

function calculateDropdownHeight(numItems: number): number {
  const visible = Math.min(Math.max(numItems, 1), MAX_VISIBLE_ITEMS);
  return visible * ITEM_HEIGHT + 8;
}

/**
 * Generic textarea with an autocomplete suggestion dropdown, keyboard navigation
 * and caret-aware insertion. It owns the interaction shell but delegates *what* to
 * suggest and *how* to insert to the `getSuggestions` / `applySuggestion` props, so
 * different features (keyword templates, filter grammar, ...) share the same look and feel.
 */
export const SuggestionTextarea = React.forwardRef<
  HTMLTextAreaElement,
  SuggestionTextareaProps
>(
  (
    {
      value,
      onChange,
      openTriggerChars = [],
      getSuggestions,
      applySuggestion,
      headerLabel,
      emptyLabel,
      className,
      onBlur,
      onFocus,
      onKeyDown,
      onOpenChange,
      ...rest
    },
    forwardedRef,
  ) => {
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const suggestionPopupRef = useRef<HTMLDivElement>(null);
    const pendingCursorRef = useRef<number | null>(null);
    const [scrollViewport, setScrollViewport] = useState<HTMLDivElement | null>(
      null,
    );

    const [isOpen, setIsOpen] = useState(false);
    const [openedBy, setOpenedBy] = useState<'type' | 'manual'>('type');
    const [cursorPosition, setCursorPosition] = useState(0);
    const [focusedIndex, setFocusedIndex] = useState(0);

    useImperativeHandle(
      forwardedRef,
      () => textAreaRef.current as HTMLTextAreaElement,
    );

    const suggestions = useMemo(() => {
      if (!isOpen) return [];
      return getSuggestions({ value, cursorPosition, openedBy });
    }, [isOpen, getSuggestions, value, cursorPosition, openedBy]);

    // Restore caret position after an insertion updates the controlled value.
    useEffect(() => {
      if (pendingCursorRef.current === null) return;
      const el = textAreaRef.current;
      const pos = pendingCursorRef.current;
      pendingCursorRef.current = null;
      if (el) {
        el.focus();
        el.setSelectionRange(pos, pos);
      }
    }, [value]);

    // Keep the focused item within view.
    useEffect(() => {
      if (!isOpen || !scrollViewport) return;
      const containerHeight = calculateDropdownHeight(suggestions.length);
      const maxVisible = Math.floor(containerHeight / ITEM_HEIGHT);
      if (focusedIndex >= maxVisible) {
        scrollViewport.scrollTop =
          (focusedIndex - maxVisible + 1) * ITEM_HEIGHT;
      } else if (focusedIndex < scrollViewport.scrollTop / ITEM_HEIGHT) {
        scrollViewport.scrollTop = focusedIndex * ITEM_HEIGHT;
      }
    }, [focusedIndex, isOpen, scrollViewport, suggestions.length]);

    useEffect(() => {
      setFocusedIndex(0);
    }, [suggestions]);

    useEffect(() => {
      onOpenChange?.(isOpen);
    }, [isOpen, onOpenChange]);

    const selectSuggestion = useCallback(
      (suggestion: AutocompleteSuggestion) => {
        const result = applySuggestion(suggestion, {
          value,
          cursorPosition,
          openedBy,
        });
        pendingCursorRef.current = result.cursorPosition;
        setCursorPosition(result.cursorPosition);
        setIsOpen(Boolean(result.keepOpen));
        onChange(result.value);
      },
      [applySuggestion, value, cursorPosition, openedBy, onChange],
    );

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const nextValue = e.target.value;
        const cursor = e.target.selectionStart ?? nextValue.length;
        setCursorPosition(cursor);
        onChange(nextValue);

        const lastChar = nextValue.slice(cursor - 1, cursor);
        if (openTriggerChars.includes(lastChar)) {
          setOpenedBy('type');
          setIsOpen(true);
        }
      },
      [onChange, openTriggerChars],
    );

    const handleSelect = useCallback(
      (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
        setCursorPosition(e.currentTarget.selectionStart ?? 0);
      },
      [],
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        onKeyDown?.(e);

        if (e.ctrlKey && e.key === ' ') {
          e.preventDefault();
          setOpenedBy('manual');
          setCursorPosition(e.currentTarget.selectionStart ?? 0);
          setIsOpen(true);
          return;
        }

        if (e.key === 'Escape' && isOpen) {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(false);
          return;
        }

        if (!isOpen) {
          if (e.key === 'ArrowDown' && suggestions.length > 0) {
            e.preventDefault();
            setIsOpen(true);
          }
          return;
        }

        if (suggestions.length === 0) return;

        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            setFocusedIndex((i) => (i + 1) % suggestions.length);
            break;
          case 'ArrowUp':
            e.preventDefault();
            setFocusedIndex(
              (i) => (i - 1 + suggestions.length) % suggestions.length,
            );
            break;
          case 'Enter':
          case 'Tab': {
            const suggestion = suggestions[focusedIndex];
            if (suggestion) {
              e.preventDefault();
              selectSuggestion(suggestion);
            }
            break;
          }
        }
      },
      [isOpen, suggestions, focusedIndex, selectSuggestion, onKeyDown],
    );

    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLTextAreaElement>) => {
        const relatedTarget = e.relatedTarget as HTMLElement | null;
        if (
          relatedTarget &&
          suggestionPopupRef.current?.contains(relatedTarget)
        ) {
          return;
        }
        setIsOpen(false);
        onBlur?.(e);
      },
      [onBlur],
    );

    const dropdownHeight = calculateDropdownHeight(suggestions.length);

    const renderSuggestionItem = (
      suggestion: AutocompleteSuggestion,
      index: number,
    ) => {
      const Icon = suggestion.icon;
      const isSelected = focusedIndex === index;
      return (
        <button
          type="button"
          className={cn(
            'w-full text-left p-2 text-sm rounded-md flex items-center gap-2 cursor-pointer hover:bg-accent',
            isSelected ? 'bg-accent' : '',
          )}
          onClick={() => selectSuggestion(suggestion)}
        >
          {suggestion.badge && (
            <span className="px-2 py-0.5 text-xs rounded-sm font-medium shrink-0 bg-muted text-muted-foreground">
              {suggestion.badge}
            </span>
          )}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {Icon && <Icon className="size-4 shrink-0" />}
            <span className="font-medium font-mono">{suggestion.label}</span>
          </div>
          {suggestion.description && (
            <span className="text-xs text-muted-foreground ml-4 truncate">
              {suggestion.description}
            </span>
          )}
        </button>
      );
    };

    return (
      <div className="relative">
        <Textarea
          {...rest}
          ref={textAreaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onSelect={handleSelect}
          onFocus={onFocus}
          onBlur={handleBlur}
          className={cn('font-mono text-sm', className)}
          autoComplete="off"
          spellCheck={false}
        />

        {isOpen && (
          <div className="absolute w-full border mt-1 bg-background rounded-md shadow-md z-50">
            <div className="relative p-4" ref={suggestionPopupRef}>
              <div className="flex justify-between mb-2">
                <h4 className="text-sm font-medium leading-none">
                  {headerLabel ?? 'Suggestions'}
                </h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsOpen(false)}
                  className="h-6 w-6 p-0"
                >
                  <PiX className="h-3 w-3" />
                </Button>
              </div>

              <div
                className="w-full"
                style={
                  {
                    '--scroll-height': `${dropdownHeight}px`,
                  } as React.CSSProperties
                }
              >
                {suggestions.length > 0 ? (
                  <InfiniteScroll
                    items={suggestions.map((suggestion, index) => ({
                      suggestion,
                      index,
                    }))}
                    renderItem={({ suggestion, index }) =>
                      renderSuggestionItem(suggestion, index)
                    }
                    limit={20}
                    observeResize={false}
                    className="pr-2 h-[var(--scroll-height)]"
                    onScrollViewportRef={setScrollViewport}
                  />
                ) : (
                  <div className="text-sm text-muted-foreground p-2">
                    {emptyLabel ?? 'No suggestions available'}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  },
);

SuggestionTextarea.displayName = 'SuggestionTextarea';
